/**
 * Module 2: Client Success Dashboard
 * 
 * Displays onboarding progress, integration status, and recommended actions
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import {
  CheckCircle2,
  AlertCircle,
  Sparkles,
  Users,
  Database,
  FileText,
  TrendingUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Maps the backend's next_action.action label (server.py's
// get_client_success_dashboard) to the module the button should open -
// the same module ids used by App.js's own left-rail navigation.
const NEXT_ACTION_MODULE = {
  'Import Leads': 'leads',
  'Generate Audit': 'audits',
  'Create Proposal': 'proposal_generator',
  'Open Revenue Engine': 'revenue_engine'
};

const ClientSuccessDashboard = ({ user, onNavigate, onContinueOnboarding }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const authHeaders = useCallback(() => ({
    'Authorization': `Bearer ${user.token}`,
    'Content-Type': 'application/json'
  }), [user.token]);

  const loadStats = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/client-success/dashboard`, {
        headers: authHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  if (loading) {
    return <div className="text-white/70">Loading...</div>;
  }

  const onboardingProgress = Math.round(stats?.onboarding_progress || 0);

  return (
    <div className="space-y-6" data-testid="client-success-dashboard">
      {/* Onboarding Progress */}
      <Card className="glass-panel border-white/10">
        <CardHeader>
          <CardTitle className="text-white/90 flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-[var(--gold)]" />
            Onboarding Progress
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/70">Overall Completion</span>
              <span className="text-sm font-semibold text-[var(--gold)]">{onboardingProgress}%</span>
            </div>
            <Progress value={onboardingProgress} className="h-3" />
            {onboardingProgress === 100 ? (
              <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                <CheckCircle2 size={14} className="mr-1" />
                Onboarding Complete
              </Badge>
            ) : (
              <Button className="primary-btn w-full mt-2" onClick={onContinueOnboarding} data-testid="continue-onboarding-btn">
                Continue Onboarding
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <section className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-panel border-white/10">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Database className="h-8 w-8 text-[var(--gold)]" />
              <div>
                <p className="text-xs text-white/60 uppercase">Leads Imported</p>
                <strong className="text-2xl font-semibold text-white">{stats?.leads_imported || 0}</strong>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-panel border-white/10">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-[var(--gold)]" />
              <div>
                <p className="text-xs text-white/60 uppercase">AI Audits</p>
                <strong className="text-2xl font-semibold text-white">{stats?.audits_generated || 0}</strong>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-panel border-white/10">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <FileText className="h-8 w-8 text-[var(--gold)]" />
              <div>
                <p className="text-xs text-white/60 uppercase">Proposals</p>
                <strong className="text-2xl font-semibold text-white">{stats?.proposals_generated || 0}</strong>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-panel border-white/10">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-[var(--gold)]" />
              <div>
                <p className="text-xs text-white/60 uppercase">Team Members</p>
                <strong className="text-2xl font-semibold text-white">{stats?.team_members || 0}</strong>
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Next Recommended Action */}
      <Card className="glass-panel border-white/10">
        <CardHeader>
          <CardTitle className="text-white/90 flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-[var(--gold)]" />
            Next Recommended Action
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="p-4 bg-[var(--gold)]/10 border border-[var(--gold)]/30 rounded-lg">
            <h4 className="font-semibold text-[var(--gold)] mb-2">
              {stats?.next_action?.title || 'Import Your First Leads'}
            </h4>
            <p className="text-sm text-white/80 mb-3">
              {stats?.next_action?.description || 'Start by importing leads to begin generating audits and proposals.'}
            </p>
            <Button
              className="primary-btn"
              onClick={() => onNavigate?.(NEXT_ACTION_MODULE[stats?.next_action?.action] || 'leads')}
              data-testid="next-action-btn"
            >
              {stats?.next_action?.action || 'Import Leads'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Integration Status */}
      <Card className="glass-panel border-white/10">
        <CardHeader>
          <CardTitle className="text-white/90">Integration Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 border border-white/10 rounded-lg">
              <div>
                <h4 className="text-sm font-semibold text-white/90">Stripe</h4>
                <p className="text-xs text-white/60">Payment processing</p>
              </div>
              <Badge className="bg-yellow-500/20 text-yellow-300 border-yellow-500/30">
                Test Mode
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 border border-white/10 rounded-lg">
              <div>
                <h4 className="text-sm font-semibold text-white/90">SendGrid</h4>
                <p className="text-xs text-white/60">Email automation</p>
              </div>
              <Badge className="bg-red-500/20 text-red-300 border-red-500/30">
                <AlertCircle size={12} className="mr-1" />
                Not Connected
              </Badge>
            </div>
            <div className="flex items-center justify-between p-3 border border-white/10 rounded-lg">
              <div>
                <h4 className="text-sm font-semibold text-white/90">AI Engine</h4>
                <p className="text-xs text-white/60">GPT-5 & Nano Banana</p>
              </div>
              <Badge className="bg-green-500/20 text-green-300 border-green-500/30">
                <CheckCircle2 size={12} className="mr-1" />
                Active
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* System Health Summary */}
      <Card className="glass-panel border-white/10">
        <CardHeader>
          <CardTitle className="text-white/90">System Health Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="text-center">
              <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-2">
                <CheckCircle2 className="h-8 w-8 text-green-400" />
              </div>
              <p className="text-xs text-white/60">API Healthy</p>
            </div>
            <div className="text-center">
              <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-2">
                <CheckCircle2 className="h-8 w-8 text-green-400" />
              </div>
              <p className="text-xs text-white/60">Database Healthy</p>
            </div>
            <div className="text-center">
              <div className="h-16 w-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-2">
                <CheckCircle2 className="h-8 w-8 text-green-400" />
              </div>
              <p className="text-xs text-white/60">All Systems Go</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ClientSuccessDashboard;
